import { Card, CardContent, CardHeader, CardTitle } from '@1trading/react';
import { ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { UuidCell } from '@/components/data-table/cells';
import { CopyButton } from '@/components/ui/copy-button';
import { LabeledValue } from '@/components/ui/labeled-value';
import { StatusChip } from '@/components/ui/status-chip';
import { ALERT_STATE_CHIP_VARIANTS, ALERT_STATE_LABELS, formatScore, SUBJECT_TYPE_SINGULAR } from '@/features/abuse/abuse-meta';
import { userConsolePath } from '@/lib/paths';
import type { AbuseSubject, AbuseSubjectDetail, AbuseSubjectKey } from '@/services/api/abuse';
import { formatDate } from '@/utils/format';

const scoreAt = (score: number | undefined, at: string | undefined): string => (score === undefined || !at ? '—' : `${formatScore(score)} at ${formatDate(at)}`);

const ScoreDetails = ({ subject }: { subject: AbuseSubject }) => (
  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
    <LabeledValue label="Current score">{formatScore(subject.score)}</LabeledValue>
    <LabeledValue label="Peak">{scoreAt(subject.peakScore, subject.peakAt)}</LabeledValue>
    <LabeledValue label="Last triggered">{scoreAt(subject.triggeredScore, subject.triggeredAt)}</LabeledValue>
    <LabeledValue label="Escalated">{scoreAt(subject.escalatedScore, subject.escalatedAt)}</LabeledValue>
  </div>
);
ScoreDetails.displayName = 'ScoreDetails';

export const SubjectHeader = ({ subjectKey, detail }: { subjectKey: AbuseSubjectKey; detail: AbuseSubjectDetail }) => (
  <Card>
    <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
      <CardTitle className="flex flex-wrap items-center gap-2">
        {SUBJECT_TYPE_SINGULAR[subjectKey.subjectType]}
        <span className="break-all font-mono text-base">{subjectKey.subjectId}</span>
        <CopyButton label={`Copy ${subjectKey.subjectId}`} value={subjectKey.subjectId} />
      </CardTitle>
      <div className="flex flex-wrap items-center gap-2">
        {detail.subject ? <StatusChip variant={ALERT_STATE_CHIP_VARIANTS[detail.subject.alertState]}>{ALERT_STATE_LABELS[detail.subject.alertState]}</StatusChip> : null}
        {subjectKey.subjectType === 'user' ? (
          <Link className="inline-flex items-center gap-1 text-sm underline underline-offset-2" rel="noreferrer" target="_blank" to={userConsolePath(subjectKey.subjectId)}>
            User console
            <ExternalLink className="size-3" />
          </Link>
        ) : null}
      </div>
    </CardHeader>
    <CardContent className="flex flex-col gap-4">
      {detail.subject ? (
        <ScoreDetails subject={detail.subject} />
      ) : (
        <p className="text-sm text-muted-foreground">No live score: it expired after a quiet period. The events below are still retained.</p>
      )}
      {detail.case ? (
        <div className="flex flex-wrap items-center gap-4 rounded-md border p-3 text-sm">
          <span className="font-medium">Compliance case</span>
          <UuidCell value={detail.case.alertId} />
          <StatusChip className="px-2 py-0.5 text-xs" variant={detail.case.status === 'active' ? 'warning' : 'muted'}>
            {detail.case.status}
          </StatusChip>
          <span className="text-muted-foreground">Severity {detail.case.severity}</span>
          <span className="text-muted-foreground">Opened {formatDate(detail.case.createdAt)}</span>
        </div>
      ) : null}
    </CardContent>
  </Card>
);
SubjectHeader.displayName = 'SubjectHeader';
